import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { TenantContext } from '../../tenant/TenantContext';
import { FormField } from '../../uiwidgets/FormField';
import { FormContainer } from '../../uiwidgets/FormContainer';
import { SubmitButton } from '../../uiwidgets/SubmitButton';
import { CancelButton } from 'uiwidgets/CancelButton';
import { Row, Col, Card, Table, Button, Modal } from 'react-bootstrap';
import { API } from '../../api/API';
import { BUSINESS_DONATION_CAUSES_URL } from '../../config/config';
import { extractError } from '../../api/ErrorUtil';
import {
    isFullFormValid, isNumeric, containsAlphaNumOnly,
    containsAlphaNumAndSpaceAndPunctuationOnly
} from 'util/Validators';
import _ from "lodash";
import { evenRound } from 'util/NumberUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCheck, faEdit, faImage, faLightbulb, faToggleOff, faToggleOn, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FilesField } from 'uiwidgets/FilesField';


const BusinessDonationCausesPage = () => {

    const imageFileInput = useRef(null);

    const initialState = {
        errors: {},
        infoMessage: "",
        id: null,
        donationCauseDescription: "",
        donationCauseCode: "",
        donationCauseAttributes: [],
        image: null,
        readOnly: false,
        editMode: false
    }
    const initialValidationState = {
        donationCauseDescriptionError: false,
        donationCauseDescriptionErrMsg: "",
        donationCauseCodeError: false,
        donationCauseCodeErrMsg: "",
        donationCauseAttributes: [],
    }

    const messageReset = () => {
        return {
            infoMessage: "",
            errors: {}
        }
    }

    const onToastClose = () => {
        setFormData({
            ...formData,
            ...messageReset()
        });
    }

    const { businessConfig } = useContext(TenantContext);
    let [donationCauses, setDonationCauses] = useState([]);
    let [formData, setFormData] = useState(initialState);
    let [formValidityData, setFormValidityData] = useState(initialValidationState);
    const [showDonationCauseImage, setShowDonationCauseImage] = useState({ show: false, donationCause: {} });
    const handleDonationCauseImageClose = () => setShowDonationCauseImage({ show: false, donationCause: {} });
    const handleDonationCauseImageShow = (donationCause) => {
        setShowDonationCauseImage({ show: true, donationCause: donationCause });
    }

    const fetchDonationCauses = useCallback(async () => {
        try {
            let dc = await API.get(BUSINESS_DONATION_CAUSES_URL + "?nonactive=true");
            let donationCauseList = dc.data

            if (!_.isEmpty(donationCauseList)) {
                donationCauseList.forEach(dcItem => {
                    if (!_.isEmpty(dcItem.donationCauseAttributes)) {
                        const updatedDonationCauseArray = dcItem.donationCauseAttributes.map(obj => ({
                            ...obj,
                            suggestedAmount: evenRound(parseFloat(obj.suggestedAmount) / 100, 2)
                        }));
                        dcItem.donationCauseAttributes = updatedDonationCauseArray;
                    }

                });
            }
            setDonationCauses(donationCauseList);
        } catch (error) {
            let { message, errors, httpStatus } = extractError(error);
            if (httpStatus === 404) {
                setDonationCauses([]);
                setFormData({ ...formData, infoMessage: "No donation causes were found" });
            } else {
                setFormData({ ...formData, errors: { message, errors, httpStatus } });
            }
        }

    })

    useEffect(() => {
        fetchDonationCauses();
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        let formValid = isFullFormValid(formValidityData)

        if (!formValid) {
            alert("Form has validation errors");
            return false;
        }
        try {

            const updatedDonationCauseArray = formData?.donationCauseAttributes?.map(obj => ({
                ...obj,
                suggestedAmount: obj.suggestedAmount * 100
            }));

            if (formData.editMode) {
                //console.log("Updating donation cause", formData);
                const postData = new FormData();
                const model = {
                    donationCauseCode: formData.donationCauseCode,
                    donationCauseDescription: formData.donationCauseDescription,
                    donationCauseAttributes: updatedDonationCauseArray
                }
                postData.append('image', formData.image);
                postData.append('model', JSON.stringify(model));
                let causeResp = await API.put(BUSINESS_DONATION_CAUSES_URL + "/" + formData.id, postData);
            } else {
                //console.log("Add new donation Cause");
                const postData = new FormData();
                const model = {
                    donationCauseCode: formData.donationCauseCode,
                    donationCauseDescription: formData.donationCauseDescription,
                    donationCauseAttributes: updatedDonationCauseArray
                }
                postData.append('image', formData.image);
                postData.append('model', JSON.stringify(model));
                let causeResp = await API.post(BUSINESS_DONATION_CAUSES_URL, postData);
            }
            setFormData({
                initialState,
                infoMessage: "Donation cause was saved successfully"
            });

            if (imageFileInput.current) {
                imageFileInput.current.value = "";
                imageFileInput.current.type = "file";
            }

        } catch (error) {
            let exError = extractError(error);
            setFormData({
                ...formData,
                errors: exError

            })
        }
        fetchDonationCauses();
    }

    const handleChange = async (event) => {
        if (formData.readOnly) {
            return;
        }
        const name = event.target.name;
        const value = event.target.value;
        setFormData({
            ...formData,
            [name]: value,
            infoMessage: "",
            errors: {}
        })
    }

    const handleFileChange = async (event) => {
        const value = event.target.files[0];
        setFormData({
            ...formData,
            image: value,
            errors: {},
            infoMessage: ""
        })
    };

    const handleValidation = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        let { hasError, error } = validateInput(name, value);
        setFormValidityData({
            ...formValidityData,
            [name + 'Error']: hasError,
            [name + 'ErrMsg']: error
        })


    }

    const handleFileValidation = async (event) => {
        if (!formData.image) {
            return;
        }
        if (formData.image.size > 300 * 1024) {
            alert("Image file size cannot exceed 100kb");
            setFormValidityData({
                ...formValidityData,
                imageError: true,
                imageErrMsg: "Image file size cannot exceed 100kb"
            })
            return false;
        }

        setFormValidityData({
            ...formValidityData,
            imageError: false,
            imageErrMsg: ""
        })
    }

    const deleteDonationCause = async (event, donationCause) => {
        try {
            let resp = await API.delete(BUSINESS_DONATION_CAUSES_URL + "/" + donationCause.id);
            setFormData({
                initialState,
                infoMessage: (resp?.data?.message) ? resp.data.message : "Donation cause was deleted"
            });

        } catch (error) {
            let exError = extractError(error);
            setFormData({
                initialState,
                errors: exError
            })
        }
        fetchDonationCauses();
    }

    const toggleDonationCauseActiveState = async (event, donationCause) => {
        try {
            let resp = await API.put(BUSINESS_DONATION_CAUSES_URL + "/" + donationCause.id + "/active");
            setFormData({
                initialState,
                infoMessage: (resp?.data?.message) ? resp.data.message : "Donation cause was state was changed"
            });

        } catch (error) {
            let exError = extractError(error);
            setFormData({
                initialState,
                errors: exError
            })
        }
        fetchDonationCauses();
    }

    const deleteDonationCauseImage = async (event, donationCause) => {
        try {
            await API.delete(BUSINESS_DONATION_CAUSES_URL + "/" + donationCause.id + "/img");
            setFormData({
                initialState,
                infoMessage: "Donation cause image was deleted successfully"
            });

        } catch (error) {
            let exError = extractError(error);
            setFormData({
                initialState,
                errors: exError
            })
        }
        fetchDonationCauses();
    }

    const editDonationCause = async (event, donationCause) => {

        setFormData({
            ...donationCause,
            editMode: true,
            errors: {},
            infoMessage: ""
        });

        let donationCauseAttrVal = [];
        let initialAttrState = {
            descriptionErrMsg: '',
            descriptionError: false,
            suggestedAmountErrMsg: '',
            suggestedAmountError: false
        }

        if (!_.isEmpty(donationCause.donationCauseAttributes)) {
            donationCause.donationCauseAttributes.forEach(element => {
                donationCauseAttrVal.push(initialAttrState);
            });
        }

        setFormValidityData({
            ...initialValidationState,
            donationCauseAttributes: donationCauseAttrVal
        })
    }
    const handleCancel = async (event) => {
        setFormData(initialState);
        setFormValidityData(initialValidationState)
    }

    const handleAttributeChange = async (event, index) => {
        const name = event.target.name;
        const value = event.target.value;
        const updatedDonCauseAttr = [...formData.donationCauseAttributes];
        updatedDonCauseAttr[index][name] = value;
        setFormData({
            ...formData,
            donationCauseAttributes: updatedDonCauseAttr,
            errors: {},
            infoMessage: ""
        })
    }

    const handleAttributeValidation = async (event, index) => {

        const name = event.target.name;
        const value = event.target.value;
        let { hasError, error } = validateInput(name, value);

        const donCauseAttribValResults = [...formValidityData.donationCauseAttributes];
        donCauseAttribValResults[index][name + 'Error'] = hasError;
        donCauseAttribValResults[index][name + 'ErrMsg'] = error

        setFormValidityData({
            ...formValidityData,
            ...donCauseAttribValResults,
        })

    }

    const validateInput = (name, value) => {
        let hasError = false,
            error = ""
        switch (name) {
            case "donationCauseCode":
                if (value.trim() === "") {
                    hasError = true
                    error = "Donation cause code cannot be empty"
                } else if (!containsAlphaNumOnly(value)) {
                    hasError = true
                    error = "Donation cause ode can only have alpha-numeric characters"
                } else if (value.length < 5) {
                    hasError = true
                    error = "Validation cause code  must be greater than 5 characters"
                }
                else {
                    hasError = false
                    error = ""
                }
                break;
            case "donationCauseDescription":
                if (value.trim() === "") {
                    hasError = true
                    error = "Donation cause description cannot be empty"
                } else if (!containsAlphaNumAndSpaceAndPunctuationOnly(value)) {
                    hasError = true
                    error = "Donation Cause description can only have alpha-numeric characters"
                } else if (value.length < 5) {
                    hasError = true
                    error = "Donation cause code description  must be greater than 5 characters"
                }
                else {
                    hasError = false
                    error = ""
                }
                break;
            case "description":
                if (value.trim() === "") {
                    hasError = false
                    error = ""
                } else if (!containsAlphaNumAndSpaceAndPunctuationOnly(value)) {
                    hasError = true
                    error = "Description can only have alpha-numeric characters"
                } else if (value.length < 5) {
                    hasError = true
                    error = "Description  must be greater than 5 characters"
                } else {
                    hasError = false
                    error = ""
                }
                break;
            case "suggestedAmount":
                if (value.trim() === "") {
                    hasError = true
                    error = "Suggested Amounts must have one to five decimal numbers, separated by commas"
                } else if (value && !isNumeric(value)) {
                    hasError = true
                    error = "Suggested Amounts must be a number"
                } else {
                    hasError = false
                    error = ""
                }
                break;
            default:
                break;
        }
        return { hasError, error };
    }

    const addDonationAttribute = async () => {
        if (!_.isEmpty(formData.donationCauseAttributes) && formData.donationCauseAttributes.length === 5) {
            alert('Number of suggestions cannot exceed five');
            return false;
        }

        if (_.isEmpty(formData.donationCauseAttributes)) {
            setFormData({
                ...formData,
                errors: {},
                infoMessage: "",
                donationCauseAttributes: [
                    {
                        suggestedAmount: 0.00,
                        description: ''
                    }
                ]
            })

            setFormValidityData({
                ...formValidityData,
                donationCauseAttributes: [
                    {
                        descriptionErrMsg: '',
                        descriptionError: false,
                        suggestedAmountErrMsg: '',
                        suggestedAmountError: false
                    }
                ]
            })

            return;

        }
        setFormData({
            ...formData,
            errors: {},
            infoMessage: "",
            donationCauseAttributes: [
                ...formData.donationCauseAttributes,
                {
                    suggestedAmount: 0.00,
                    description: ''
                }
            ]
        })


        setFormValidityData({
            ...formValidityData,
            donationCauseAttributes: [
                ...formValidityData.donationCauseAttributes,
                {
                    descriptionErrMsg: '',
                    descriptionError: false,
                    suggestedAmountErrMsg: '',
                    suggestedAmountError: false
                }
            ]
        })

    }

    const removeDonationAttribute = async (evt, index) => {
        let updatedDonCauseAttr = formData.donationCauseAttributes.filter((item, i) => i !== index);
        let updatedDonCauseAttrVals = formValidityData.donationCauseAttributes.filter((item, i) => i !== index);
        setFormData({
            ...formData,
            donationCauseAttributes: updatedDonCauseAttr
        })
        setFormValidityData({
            ...formValidityData,
            donationCauseAttributes: updatedDonCauseAttrVals
        })
    }

    return (<>
        <FormContainer title="Donation Causes" submitButtonFunction={handleSubmit}
            errors={formData.errors} infoMessage={formData.infoMessage} toastCloseFunc={onToastClose}>
            <Row>
                <Col mb={4} lg={4} sm={12}>
                    <FormField name="donationCauseDescription" label="Description" type="text" value={formData.donationCauseDescription}
                        placeholder="Enter donation cause description" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.donationCauseDescriptionError} valMsg={formValidityData.donationCauseDescriptionErrMsg}
                        readOnly={formData.readOnly} />
                </Col>
                <Col mb={4} lg={4} sm={12}>
                    <FormField name="donationCauseCode" label="Donation Cause Code" type="text" value={formData.donationCauseCode}
                        placeholder="Enter Donation Cause Code" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.donationCauseCodeError} valMsg={formValidityData.donationCauseCodeErrMsg}
                        readOnly={formData.editMode} />
                </Col>
            </Row>
            <Row>
                <Col>

                    <FilesField name="image" label="Image" placeholder="Image" accept="image/*" onChange={handleFileChange} rref={imageFileInput}
                        onBlur={handleFileValidation} valstate={!formValidityData.imageError} valMsg={formValidityData.imageErrMsg} />
                </Col>

            </Row>
            <Row><Col>
                <Button variant="info" className="btn-fill" onClick={addDonationAttribute}>
                    Add Suggestion
                </Button>
            </Col>
            </Row>
            <Table>
                <tbody>
                    {formData.donationCauseAttributes && formData.donationCauseAttributes.map((donationCauseAttr, index) => (
                        <React.Fragment key={index}>
                            <tr>
                                <td width="15%">
                                    <FormField name="suggestedAmount" label="Suggested Amount" type="text" value={donationCauseAttr.suggestedAmount}
                                        placeholder="Enter Suggested Amount" onChange={(e) => handleAttributeChange(e, index)}
                                        onBlur={(e) => handleAttributeValidation(e, index)}
                                        valstate={!formValidityData.donationCauseAttributes[index].suggestedAmountError}
                                        valMsg={formValidityData.donationCauseAttributes[index].suggestedAmountErrMsg}
                                        readOnly={formData.readOnly} />
                                </td>
                                <td>
                                    <FormField name="description" label="Amount Description" type="text" value={donationCauseAttr.description}
                                        placeholder="Enter suggested amount description" onChange={(e) => handleAttributeChange(e, index)}
                                        onBlur={(e) => handleAttributeValidation(e, index)}
                                        valstate={!formValidityData.donationCauseAttributes[index].descriptionError} valMsg={formValidityData.donationCauseAttributes[index].descriptionErrMsg}
                                        readOnly={formData.readOnly} />
                                </td>
                                <td width="10%">
                                    <br />
                                    <Button variant="dark" className="border-0 text-dark" onClick={evt => removeDonationAttribute(evt, index)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </td>
                            </tr>
                        </React.Fragment>
                    ))}
                </tbody>
            </Table>
            <Row>
                <Col>
                    <SubmitButton label='Save Donation Cause' disabled={formData.readOnly} className="me-4" />
                    <CancelButton label='Cancel' onClick={handleCancel} />
                </Col>
            </Row>

        </FormContainer>
        <Card>
            <Card.Body>
                <Table className="table-hover table-striped">
                    <thead>
                        <tr>
                            <th> Image</th>
                            <th className="border-0">Donation Cause Code</th>
                            <th className="border-0">Donation Cause Description</th>
                            <th className="border-0">State</th>
                            <th className="border-0">Sponsorship<br /> Cause</th>
                            <th className="border-0">Edit</th>
                            <th className="border-0">Delete Cause</th>
                            <th className="border-0">Toggle State</th>
                            <th className="border-0">Delete Image</th>
                        </tr>

                    </thead>
                    <tbody>
                        {donationCauses && !_.isEmpty(donationCauses) && donationCauses.map((donationCause, idx) => (
                            <tr key={idx}>
                                <td>
                                    {donationCause.mediaType &&
                                        <Button variant="dark" className="border-0 text-dark" onClick={event => handleDonationCauseImageShow(donationCause)}>
                                            <FontAwesomeIcon icon={faImage} />
                                        </Button>}
                                </td>
                                <td>{donationCause.donationCauseCode}</td>
                                <td>{donationCause.donationCauseDescription}</td>
                                <td>{donationCause.active ? 'Active' : 'Deactivated'}</td>
                                <td align="center">{donationCause.sponsorshipCause && <FontAwesomeIcon size="lg" color="green" icon={faCheck} />}</td>

                                <td><Button variant="dark" className="border-0 text-dark" onClick={evt => editDonationCause(evt, donationCause)}
                                    disabled={!donationCause.active}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button></td>
                                <td>
                                    {donationCause.active && <Button variant="dark" className="border-0 text-dark" onClick={evt => deleteDonationCause(evt, donationCause)}
                                        disabled={!donationCause.active}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>}

                                </td>
                                <td>
                                    <Button variant="dark" className="border-0 text-dark" onClick={evt => toggleDonationCauseActiveState(evt, donationCause)}>
                                        {!donationCause.active && <FontAwesomeIcon icon={faToggleOn} />}
                                        {donationCause.active && <FontAwesomeIcon icon={faToggleOff} />}
                                    </Button>
                                </td>
                                <td>{donationCause.mediaType && <Button variant="dark" className="border-0 text-dark" onClick={evt => deleteDonationCauseImage(evt, donationCause)}
                                    disabled={!donationCause.active}>
                                    <span className="fa-stack">
                                        <FontAwesomeIcon icon={faImage} className='fa-solid fa-stack-1x'  title="delete image"/>
                                        <FontAwesomeIcon icon={faBan} className='fa-solid fa-stack-2x' style={{ color: "Tomato" }} title="delete image"/>
                                    </span>
                                </Button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
        <Modal show={showDonationCauseImage.show} onHide={handleDonationCauseImageClose}>
            <Modal.Header closeButton />
            <Modal.Body>
                {showDonationCauseImage.donationCause.mediaType &&
                    <img height="450px" width="450px" src={process.env.REACT_APP_BASE_API_URL + BUSINESS_DONATION_CAUSES_URL + '/' + showDonationCauseImage.donationCause.id + '/img'} />}
                {(!showDonationCauseImage.donationCause.mediaType) &&
                    <>No Image is assinged to this donation cause</>
                }
            </Modal.Body>
        </Modal>
    </>
    )

}

export default BusinessDonationCausesPage